import type { SVGProps } from 'react';

const outlineProps = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const filledProps = {
  fill: 'currentColor',
  stroke: 'none' as const,
};

export function NavIcon({
  name,
  className = 'h-6 w-6 shrink-0',
  filled = false,
  ...rest
}: { name: keyof typeof iconsOutline; className?: string; filled?: boolean } & SVGProps<SVGSVGElement>) {
  const useStrokeActive = filled && name === 'nutrition';
  const paths = filled && !useStrokeActive ? iconsFilled[name] : iconsOutline[name];
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden
      {...(useStrokeActive ? { ...outlineProps, strokeWidth: 2.25 } : filled ? filledProps : outlineProps)}
      {...rest}
    >
      {paths}
    </svg>
  );
}

const iconsOutline = {
  home: (
    <>
      <path d="M4 10.5 12 4l8 6.5V19a1.5 1.5 0 0 1-1.5 1.5H15v-6.5H9V20.5H5.5A1.5 1.5 0 0 1 4 19v-8.5z" />
    </>
  ),
  dayReview: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </>
  ),
  nutrition: (
    <>
      <path d="M5.5 3v6.5a2.5 2.5 0 0 0 5 0V3" />
      <path d="M8 9.5V21" />
      <path d="M18 3v18" />
      <path d="M18 3c-2.5 2.5-2.5 5.5 0 7.5" />
    </>
  ),
  sleep: (
    <>
      <path d="M21 14.5A8.5 8.5 0 0 1 8.5 6 8.5 8.5 0 0 0 21 14.5z" />
    </>
  ),
  workouts: (
    <>
      <path d="M3 10.5v3a2 2 0 0 0 2 2h1.5v-7H5a2 2 0 0 0-2 2z" />
      <path d="M17.5 10.5v3a2 2 0 0 0 2 2H21v-7h-1.5a2 2 0 0 0-2 2z" />
      <path d="M9.5 12h5" strokeWidth={2.25} />
    </>
  ),
  progress: (
    <>
      <path d="M18 20V10M12 20V4M6 20v-6" strokeWidth={2.25} />
    </>
  ),
  coach: (
    <>
      <path d="M12 2.5 13.4 7.1 18 8.5 13.4 9.9 12 14.5 10.6 9.9 6 8.5 10.6 7.1 12 2.5z" />
      <path d="M19.5 15.5 20.3 18.1 22.9 18.9 20.3 19.7 19.5 22.3 18.7 19.7 16.1 18.9 18.7 18.1 19.5 15.5z" />
      <path d="M5 17.5 5.6 19.3 7.4 19.9 5.6 20.5 5 22.3 4.4 20.5 2.6 19.9 4.4 19.3 5 17.5z" />
    </>
  ),
  profile: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" />
    </>
  ),
  more: (
    <>
      <rect x="4" y="4" width="6.5" height="6.5" rx="1.75" />
      <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.75" />
      <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.75" />
      <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.75" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </>
  ),
  circles: (
    <>
      <circle cx="12" cy="8" r="4.5" />
      <circle cx="6" cy="16" r="3" />
      <circle cx="18" cy="16" r="3" />
      <path d="M9.2 11.2 7.2 14M14.8 11.2l2 2.8" />
    </>
  ),
} as const;

const iconsFilled = {
  home: (
    <>
      <path d="M12 3.2 3 10.5V19a2 2 0 0 0 2 2h5v-7h4v7h5a2 2 0 0 0 2-2v-8.5L12 3.2z" />
    </>
  ),
  dayReview: (
    <>
      <path d="M6 2a1 1 0 0 0-1 1v1H5a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3h-.5V3a1 1 0 1 0-2 0v1h-5V3a1 1 0 1 0-2 0v1H7V3a1 1 0 0 0-1-1z" />
    </>
  ),
  nutrition: (
    <>
      <path d="M5.25 2A1.75 1.75 0 0 0 3.5 3.75V8a4.5 4.5 0 0 0 9 0V3.75A1.75 1.75 0 0 0 10.75 2h-1v7.5a1.25 1.25 0 1 1-2.5 0V2h-1z" />
      <path d="M16.25 2A1.75 1.75 0 0 0 14.5 3.75V20a1.75 1.75 0 0 0 3.5 0V3.75A1.75 1.75 0 0 0 16.25 2z" />
      <path d="M18 4c-2 1.8-2 4.2 0 6V4z" opacity="0.9" />
    </>
  ),
  sleep: (
    <>
      <path d="M21 14.5A8.5 8.5 0 0 1 8.5 6 8.5 8.5 0 0 0 21 14.5z" />
    </>
  ),
  workouts: (
    <>
      <path d="M3 9.5a2.5 2.5 0 0 1 2.5-2.5H6v9H5.5A2.5 2.5 0 0 1 3 13.5v-4z" />
      <path d="M21 9.5a2.5 2.5 0 0 0-2.5-2.5H18v9h.5a2.5 2.5 0 0 0 2.5-2.5v-4z" />
      <rect x="8.5" y="10.25" width="7" height="3.5" rx="1" />
    </>
  ),
  progress: (
    <>
      <rect x="5" y="14" width="4" height="8" rx="1" />
      <rect x="11" y="4" width="4" height="18" rx="1" />
      <rect x="17" y="10" width="4" height="12" rx="1" />
    </>
  ),
  coach: (
    <>
      <path d="M12 1.5 14.2 7.8 20.5 10 14.2 12.2 12 18.5 9.8 12.2 3.5 10 9.8 7.8 12 1.5z" />
      <path d="M20.2 15.8 21 18.2 23.4 19 21 19.8 20.2 22.2 19.4 19.8 17 19 19.4 18.2 20.2 15.8z" />
      <path d="M5.5 17.2 6.2 19.2 8.2 19.9 6.2 20.6 5.5 22.6 4.8 20.6 2.8 19.9 4.8 19.2 5.5 17.2z" />
    </>
  ),
  profile: (
    <>
      <circle cx="12" cy="8" r="4.5" />
      <path d="M4.5 20.5c0-4 3.6-7 7.5-7s7.5 3 7.5 7v1.5H4.5V20.5z" />
    </>
  ),
  more: (
    <>
      <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="2" />
      <rect x="13" y="3.5" width="7.5" height="7.5" rx="2" />
      <rect x="3.5" y="13" width="7.5" height="7.5" rx="2" />
      <rect x="13" y="13" width="7.5" height="7.5" rx="2" />
    </>
  ),
  settings: (
    <>
      <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  circles: (
    <>
      <circle cx="12" cy="7.5" r="5" />
      <circle cx="5.5" cy="17" r="3.5" />
      <circle cx="18.5" cy="17" r="3.5" />
    </>
  ),
} as const;

export const navTabAccent: Partial<Record<keyof typeof iconsOutline, string>> = {
  home: 'text-white',
  nutrition: 'text-emerald-400',
  workouts: 'text-sky-400',
  coach: 'text-violet-400',
  more: 'text-zinc-300',
};

export const navTabGlow: Partial<Record<keyof typeof iconsOutline, string>> = {
  home: 'drop-shadow-[0_0_10px_rgba(255,255,255,0.25)]',
  nutrition: 'drop-shadow-[0_0_10px_rgba(52,211,153,0.4)]',
  workouts: 'drop-shadow-[0_0_10px_rgba(56,189,248,0.4)]',
  coach: 'drop-shadow-[0_0_10px_rgba(167,139,250,0.45)]',
  more: 'drop-shadow-[0_0_8px_rgba(161,161,170,0.2)]',
};
